import { env } from "@/env/client";
import Script from "next/script";
import { type ChangeEventHandler, useEffect, useRef } from "react";
import usePlacesAutocomplete, { getDetails } from "use-places-autocomplete";

type Result = {
  addressLine1?: string;
  postalCode?: string;
  city?: string;
  country?: string;
};

type Props = {
  onSelect: (result: Result) => void;
  defaultValue?: string;
};
export const Autocomplete = ({ onSelect, defaultValue }: Props) => {
  const ref = useRef<HTMLInputElement>(null);
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    defaultValue: "",
    callbackName: "YOUR_CALLBACK_NAME",
    requestOptions: {
      types: ["address"],
    },
    debounce: 300,
  });

  useEffect(() => {
    if (defaultValue && ready) setValue(defaultValue);
  }, [defaultValue, setValue, ready]);

  const handleInput: ChangeEventHandler<HTMLInputElement> = (e) => {
    setValue(e.target.value);
  };

  const handleSelect = (a: google.maps.places.AutocompletePrediction) => {
    clearSuggestions();

    getDetails({ placeId: a.place_id }).then((result) => {
      if (typeof result !== "string") {
        const streetNumber = result.address_components?.find((ac) => ac.types.includes("street_number"))?.long_name;
        const addressLine1 = result.address_components?.find((ac) => ac.types.includes("route"))?.long_name;
        const postalCode = result.address_components?.find((ac) => ac.types.includes("postal_code"))?.long_name;
        const city = result.address_components?.find((ac) => ac.types.includes("locality"))?.long_name;
        const country = result.address_components?.find((ac) => ac.types.includes("country"))?.short_name;
        onSelect({ addressLine1: `${streetNumber || ""} ${addressLine1}`.trim(), postalCode, city, country });
      }
    });
  };

  const renderSuggestions = () =>
    data.map((suggestion) => {
      const {
        place_id,
        structured_formatting: { main_text, secondary_text },
      } = suggestion;

      return (
        <li key={place_id} onClick={() => handleSelect(suggestion)} className="cursor-pointer p-2 hover:bg-primary/10">
          <strong>{main_text}</strong> <small>{secondary_text}</small>
        </li>
      );
    });

  return (
    <div>
      <Script
        defer
        src={`https://maps.googleapis.com/maps/api/js?key=${env.googleMapsApiKey}&libraries=places&callback=YOUR_CALLBACK_NAME`}
      />
      <div>
        <label htmlFor="address-autocomplete" className="text-base font-bold text-[#22201F]">
          Entrez votre adresse*
        </label>
        <div className="relative mt-2">
          <input
            ref={ref}
            id="address-autocomplete"
            value={value}
            onChange={handleInput}
            disabled={!ready}
            placeholder="ex : 10 rue de Mézière 75002 Paris"
            className="bg-white rounded-lg border py-3 px-4 w-full text-base font-normal placeholder:text-[#B8B8B8] text-[#22201F] border-[#E5E5E5]"
            autoComplete="nope"
          />
          {status === "OK" && (
            <ul className="absolute z-10 top-[calc(100%+8px)] right-0 left-0 bg-white border rounded-lg text-[#22201F] border-[#E5E5E5] flex flex-col divide-y divide-[#E5E5E5]">
              {renderSuggestions()}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
